import React, { useState } from "react"
import { Stack } from "@mui/material"
import { displayedName, getColorForJob, MultilangJob, updateExternalProjectId } from "src/services/api/document"
import { useTranslation } from "react-i18next"
import { ThickDivider } from "./ThickDivider"
import { SingleLangJobs } from "./SingleLangJobs"
import { MoreInfo } from "./MoreInfo"
import { FixedPrice } from "./FixedPrice"
import { BasicInfo } from "./BasicInfo"
import CustomDrawer from "src/components/CustomDrawer"
import { useSnackbar } from "src/providers/SnackbarProvider"
import { DrawerHeader } from "src/km/components/DrawerHeader"
import { CloseModalIconButton } from "src/components/CloseModalIconButton"
import { FileCardLabel } from "../FileCardLabel"

interface JobInfoDrawerProps {
  multilangJob: MultilangJob
  setMultilangJob: (multilangJob?: MultilangJob) => void
  onJobUpdated?: (multilangJob: MultilangJob) => void
}

export const JobInfoDrawer = ({ multilangJob, setMultilangJob, onJobUpdated }: JobInfoDrawerProps) => {
  const isJobQuoted = ["QUOTED", "QUOTING"].includes(multilangJob.status)
  const [showMoreOpened, setShowMoreOpened] = useState(isJobQuoted ? undefined : false)
  const [savingProjectId, setSavingProjectId] = useState(false)
  const { t } = useTranslation("multilang")
  const { snackbarShowMessage } = useSnackbar()
  const onShowMoreClick = () => {
    setShowMoreOpened(!showMoreOpened)
  }

  const onClose = () => {
    setMultilangJob(undefined)
    setShowMoreOpened(false)
  }

  const onReopenClick = () => {
    onClose()
    snackbarShowMessage("Project reopened", "success")
  }

  const onSaveProjectId = async (value: string) => {
    setSavingProjectId(true)
    try {
      await updateExternalProjectId(multilangJob.id, value)
      const normalized = value.trim() === "" ? undefined : value
      const updatedJob = { ...multilangJob, externalProjectId: normalized }
      setMultilangJob(updatedJob)
      onJobUpdated?.(updatedJob)
      snackbarShowMessage("Project ID updated", "success")
    } catch {
      snackbarShowMessage("Failed to update Project ID", "error")
      throw new Error("Failed to update Project ID")
    } finally {
      setSavingProjectId(false)
    }
  }

  const labelColor = getColorForJob(multilangJob)
  const showSingleLangJobs = !["QUOTING", "QUOTED"].includes(multilangJob.status)
  const showProjectIdEdit = ["QUOTING", "QUOTED", "IN_PROGRESS", "FINISHED"].includes(multilangJob.status)
  const hasFinishedSingleLangJob = multilangJob.singleLangJobs.some((job) => job.status === "FINISHED")
  const projectIdEditDisabled = hasFinishedSingleLangJob

  return (
    <CustomDrawer
      open={!!multilangJob}
      onClose={onClose}
      topSectionColor={labelColor}
      topBar={
        <Stack direction={"row"} alignItems={"center"} justifyContent={"flex-end"}>
          <FileCardLabel job={multilangJob} />
          <CloseModalIconButton onClose={() => setMultilangJob(undefined)} />
        </Stack>
      }
      topSection={
        <>
          <DrawerHeader
            header={multilangJob.externalProjectId ? t("projectId") : t("projectName")}
            value={displayedName(multilangJob)}
            editable={showProjectIdEdit}
            editDisabled={projectIdEditDisabled}
            editDisabledTooltip={projectIdEditDisabled ? t("projectIdEditDisabledFinished") : undefined}
            saving={savingProjectId}
            onSave={onSaveProjectId}
          />
          <ThickDivider />
          <BasicInfo multilangJob={multilangJob} showMoreOpened={showMoreOpened} onShowMoreClick={onShowMoreClick} />
          <MoreInfo open={showMoreOpened !== false} multilangJob={multilangJob} color={labelColor} />
          <ThickDivider />
          <FixedPrice multilangJob={multilangJob} />
        </>
      }
      bottomSection={
        showSingleLangJobs && (
          <SingleLangJobs multilangJob={multilangJob} onReopenClick={onReopenClick} overflowAuto={!showMoreOpened} />
        )
      }
    />
  )
}
